import React, { useState, useEffect } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Icon,
  Text,
  Tooltip,
  IconButton,
  Menu,
  MenuButton,
  Portal,
  useDisclosure
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { IoMdAdd } from "react-icons/io";
import { FiFilter } from "react-icons/fi";
import { NodeInsights } from "containers/Interfaces";
import { EditTreeSuggestions } from "./EditTreeSuggestions";
import { useUser } from "context/UserContext";

import {
  buildActualsMap,
  actualsIdToMetricName,
  buildBenchmarksIdsToNameMap,
  getNonMeasureData,
  formatWithProto,
  getMetricFormat,
  isMaterialColumn,
  showFilterForMetric,
  showFilterForVersusBenchmark,
  ExpandedColumns,
  getAnalysisColumnDisplayName,
  Suggestions,
  SuggestionMap,
  getMaterialIdForMetric
} from "./dataTableUtils";
import { MeltedDataTable } from "./MeltedDataTable";
import { KGObservationProto } from "build-ts/analysis_plan";
import { getKGGrowthBenchmarks, mergeMetricEdgesByType } from "utils/analysisUtils";
import { MetricProto } from "build-ts/metadata";
import { timeBucketToJSON } from "build-ts/enums";
import { formatToSentenceCase } from "utils/nameUtils";
import { GrowthBenchmarkType } from "build-ts/relative_date";
import { ObservationUtils } from "utils/ObservationUtils";

export const DataTableMKG: React.FC<{ 
  node: NodeInsights,
  isChatOpen: boolean, 
  metrics?: Map<string, MetricProto>,
  treePannelLoader?: boolean,
  showEditSuggestions?: boolean,
  isOverlay?: boolean
}> = ({ node, isChatOpen, metrics = new Map(), treePannelLoader = false, showEditSuggestions = true, isOverlay = false }) => {
  const [expanded, setExpanded] = useState<ExpandedColumns>({
    actuals: new Set(),
    benchmarks: new Set()
  });
  const [melted, setMelted] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [selectedMeltedRowId, setSelectedMeltedRowId] = useState<string | null>(null);
  const [newSuggestionMap, setNewSuggestionMap] = useState<SuggestionMap>({});
  const [listOfSuggestions, setListOfSuggestions] = useState<Suggestions>({
    attributes: [],
    measures: [],
    metricEdges: [],
    benchmarks: [],
    temporal: [],
    metrics: []
  });
  const { onOpen, onClose } = useDisclosure();
  const { defaultTimeZone, quarterStartMonth } = useUser();
  // access kgObservation and kgObservationData from the node
  const kgObservation = node.current_node?.kg_observation;
  const kgObservationData = node.current_node?.kg_observation_data;
  const actualsMap = kgObservationData ? buildActualsMap(kgObservationData) : {};
  const actualsIdNameMap = actualsIdToMetricName(node?.current_node);
  const benchmarksIdNameMap = buildBenchmarksIdsToNameMap(node?.current_node);
  const { nonMeasureColumns, nonMeasureValues } = getNonMeasureData(node, defaultTimeZone, quarterStartMonth);

  // Helper function to get material name for display
  const getMaterialNameForMetric = (metricId: string) => {
    const materialId = getMaterialIdForMetric(metricId, node.current_node);
    const materialName = node.current_node?.kg_observation_data?.kg_observation?.metrics.find(m=>m.material?.header?.id === materialId)?.material?.header?.name;
    const isMaterialColumnAMetric = node.current_node?.kg_observation?.metrics.find(m => m.header?.id === materialId);
    if(isMaterialColumnAMetric){
      return false;
    }
    return materialName;
  };

  // Check if the table is melted or not
  useEffect(() => {
    const isMelted = nonMeasureColumns?.length === 0;
    setMelted(isMelted);

  }, [nonMeasureColumns]);

  // Function to fetch suggestions for edit tree
  const fetchSuggestions = async (kgObservation: KGObservationProto) => {
    const currentMetrics = kgObservation?.metrics?.map(metric => 
      metrics.get(metric.header?.id!))
      .filter((metric): metric is MetricProto => metric !== undefined);

    // it will returns combined edges based on edge type
    const list = mergeMetricEdgesByType(currentMetrics || []);

    // it will return growth benchmarks except LAST_N_PERIOD_AGGREGATION
    const growth_benchmarks = getKGGrowthBenchmarks([]).filter(benchmark => 
      benchmark.growth_benchmark?.growth_type !== GrowthBenchmarkType.LAST_N_PERIOD_AGGREGATION
    );

    // Setup suggestions by category
    const listOfSuggestions = {
      attributes: list.attributeHeaders.filter(h => h.id && h.name),
      measures: list.measureHeaders.filter(h => h.id && h.name),
      metricEdges: list.metricEdgeHeaders.filter(h => h.id && h.name),
      benchmarks: [
        ...list.benchmarkEntities
          .filter(entity => entity.header?.id && entity.header?.name),
        ...growth_benchmarks
          .filter(entity => entity.header?.id && entity.header?.name)
      ],
      temporal: list.temporalHeaders.filter(h => h.id && h.name),
      metrics: list.metricsHeaders.filter(h => h.id && h.name)
    };

    // Create suggestion map
    const newMap: SuggestionMap = {};

    listOfSuggestions.attributes.forEach(header => {
      const column = node.current_node.kg_observation?.columns.find(column => column.kg_column_header?.id === header.id);
      newMap[header?.id!] = {
        observationColumnProto: column ? column : ObservationUtils.getObservationColumns([header])[0],
        isPresent: !!column,
        isSelected: !!column
      };
    });

    listOfSuggestions.measures.forEach(header => {
      const column = node.current_node.kg_observation?.columns.find(column => column.kg_column_header?.id === header.id);
      newMap[header?.id!] = {
        observationColumnProto: column ? column :  ObservationUtils.getObservationColumns([header])[0],
        isPresent: !!column,
        isSelected: !!column
      };
    });

    listOfSuggestions.temporal.forEach(header => {
      const column = node.current_node.kg_observation?.columns.find(column => column.kg_column_header?.id === header.id);
      newMap[header?.id!] = {
        observationColumnProto: column ? column :  ObservationUtils.getObservationColumns([header])[0],
        isPresent: !!column,
        isSelected: !!column
      };
    });

    listOfSuggestions.metricEdges.forEach(header => {
      const isPresent = node.current_node.kg_observation?.metrics.some(metric => metric.header?.id === header.id) || false;
      newMap[header?.id!] = {
        metricProto: MetricProto.create({
          header: header,
        }),
        isPresent,
        isSelected: isPresent
      };
    });

    listOfSuggestions.metrics.forEach(header => {
      const isPresent = node.current_node.kg_observation?.metrics.some(metric => metric.header?.id === header.id) || false;
      newMap[header?.id!] = {
        metricProto: MetricProto.create({
          header: header,
        }),
        isPresent,
        isSelected: isPresent
      };
    });

    listOfSuggestions.benchmarks.forEach(benchmark => {
      if (benchmark?.growth_benchmark?.growth_type === GrowthBenchmarkType.PRIOR_PERIOD){
        const priorPeriodBenchmark = node.current_node.kg_observation?.benchmarks.find(benchmark => 
          benchmark.growth_benchmark?.growth_type === GrowthBenchmarkType.PRIOR_PERIOD
        );
        const benchmarkId = priorPeriodBenchmark?.header?.id || benchmark.header?.id;
        if(priorPeriodBenchmark){
          benchmark.header = priorPeriodBenchmark.header;
        }
        newMap[benchmarkId!] = {
          benchmarkEntityProto: priorPeriodBenchmark ? priorPeriodBenchmark : benchmark,
          isPresent: !!priorPeriodBenchmark,
          isSelected: !!priorPeriodBenchmark
        };
      } else if (benchmark?.growth_benchmark?.growth_type === GrowthBenchmarkType.PRIOR_YEAR){
        const priorYearBenchmark = node.current_node.kg_observation?.benchmarks.find(benchmark => 
          benchmark.growth_benchmark?.growth_type === GrowthBenchmarkType.PRIOR_YEAR
        );
        const benchmarkId = priorYearBenchmark?.header?.id || benchmark.header?.id;
        if(priorYearBenchmark){
          benchmark.header = priorYearBenchmark.header;
        }
        newMap[benchmarkId!] = {
          benchmarkEntityProto: priorYearBenchmark ? priorYearBenchmark : benchmark,
          isPresent: !!priorYearBenchmark,
          isSelected: !!priorYearBenchmark
        };
      } else {
        const benchmarkId = benchmark.header?.id;
        newMap[benchmarkId!] = {
          benchmarkEntityProto: benchmark,          
          isPresent: !!benchmark,
          isSelected: !!benchmark
        };
      }     
    });

    setNewSuggestionMap(newMap);

    setListOfSuggestions(listOfSuggestions);
  };

  useEffect(() => {
    if(node.current_node.kg_observation){
      fetchSuggestions(node.current_node.kg_observation);
    }
  }, [node.current_node.kg_observation]);

  // Sort actuals by type (metric first, then measure) and filter out material entries
  const sortActualsByType = (entries: [string, any][]): [string, any][] => {
    if (!actualsIdNameMap) return entries;
    
    return [...entries]
      .sort((a, b) => {
        const typeA = actualsIdNameMap[a[0]]?.type;
        const typeB = actualsIdNameMap[b[0]]?.type;
        
        if (typeA === "metric" && typeB !== "metric") return -1;
        if (typeA !== "metric" && typeB === "metric") return 1;
        
        return actualsIdNameMap[a[0]]?.name.localeCompare(actualsIdNameMap[b[0]]?.name);
      });
  };

  // Sort benchmarks
  const sortBenchmarks = (entries: [string, any][]): [string, any][] => {
    return [...entries].sort((a, b) => a[0].localeCompare(b[0]));
  };

  // Sort analysis types to show variance first, then variance percentage
  const sortAnalysisTypes = (entries: [string, any][]): [string, any][] => {
    return [...entries].sort(([typeA], [typeB]) => {
      if (typeA === "VARIANCE" && typeB !== "VARIANCE") return -1;
      if (typeA !== "VARIANCE" && typeB === "VARIANCE") return 1;
      return 0;
    });
  };

  const toggleActual = (actualId: string) => {
    setExpanded(prev => {
      const newExpanded = { ...prev };
      
      if (newExpanded.actuals.has(actualId)) {
        newExpanded.actuals.delete(actualId);
        // Collapse all benchmarks for this actual
        const filteredBenchmarks = new Set<string>();
        newExpanded.benchmarks.forEach(b => {
          if (!b.startsWith(actualId)) {
            filteredBenchmarks.add(b);
          }
        });
        newExpanded.benchmarks = filteredBenchmarks;
      } else {
        newExpanded.actuals.add(actualId);
      }
      
      return newExpanded;
    });
  };

  const toggleBenchmark = (benchmarkKey: string) => {
    setExpanded(prev => {
      const newExpanded = { ...prev };
      
      if (newExpanded.benchmarks.has(benchmarkKey)) {
        newExpanded.benchmarks.delete(benchmarkKey);
      } else {
        newExpanded.benchmarks.add(benchmarkKey);
      }
      
      return newExpanded;
    });
  };

  const handleMenuOpen = (rowIndex: number, meltedRowId?: string) => {
    if (melted) {
      setSelectedMeltedRowId(meltedRowId || null);
    } else {
      setSelectedRowIndex(rowIndex);
    }
    onOpen();
  };

  const handleMenuClose = () => {
    if (melted) {
      setSelectedMeltedRowId(null);
    } else {
      setSelectedRowIndex(null);
    }
    onClose();
  };

  const renderTableHeader = () => {
    if (!actualsMap || !actualsIdNameMap) return null;

    const sortedActuals = sortActualsByType(Object.entries(actualsMap));

    return (
      <Tr>
        {nonMeasureColumns?.map((col, index) => (
          <Th 
          key={col.id} 
          height="40px" 
          minHeight="40px" 
          verticalAlign="middle" 
          fontSize="sm" 
          p={2} 
          whiteSpace="nowrap" 
          overflow="hidden" 
          textOverflow="ellipsis"
          width={index === 0 ? "200px" : "150px"}
          minWidth={index === 0 ? "150px" : "120px"}
          maxWidth={index === 0 ? "300px" : "200px"}
          position="sticky"
          top="0"
          bg="white"
          zIndex={index === 0 ? 20 : 16}
          left={index === 0 ? "0" : "auto"}
          borderBottom="1px solid"
          borderColor="gray.200"
          fontWeight="600"
          >
            {col.isTemporal ? 
              (col.timeBucket ? `${formatToSentenceCase(timeBucketToJSON(col.timeBucket))} (Date)` : "Date") 
              : col.name}
          </Th>
        ))}

        {sortedActuals?.map(([actualId, actualData]) => (
          <React.Fragment key={actualId}>
            <Th 
              cursor="pointer" 
              onClick={() => toggleActual(actualId)}
              color="gray.600"
              fontSize={isChatOpen ? "2xs" : "xs"}
              bg="gray.50"
              fontWeight="700"
              textAlign="right"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              position="sticky"
              top="0"
              px={2}
              py={isChatOpen ? 1 : 1}
              left="auto"
              zIndex={15}
              width="140px"
              minWidth="120px"
              maxWidth="180px"
              borderBottom="1px solid"
              borderColor="gray.200"
            >
              {actualsIdNameMap[actualId]?.name}{" "}
              {actualsIdNameMap && actualsIdNameMap[actualId]?.type === "metric" && (
                <Tooltip
                  label={showFilterForMetric(actualId, kgObservation!)}
                  placement="top"
                >
                  <IconButton 
                    aria-label="Filters"
                    icon={<FiFilter size="10px" />}
                    size="xs"
                    variant="ghost"
                    minW="16px"
                    height="16px"
                    p={0}
                  />
                </Tooltip>
              )}
              {expanded.actuals.has(actualId) ? <ChevronDownIcon ml={1} /> : <ChevronRightIcon ml={1} />}
            </Th>

            {expanded.actuals.has(actualId) && (
              <>
                {/* Material column - show if this actual has a material */}
                {getMaterialNameForMetric(actualId) && (
                  <Th 
                    color="gray.600"
                    fontSize={isChatOpen ? "2xs" : "xs"}
                    bg="gray.50"
                    fontWeight="700"
                    textAlign="right"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    position="sticky"
                    top="0"
                    px={2}
                    py={isChatOpen ? 1 : 1}
                    left="auto"
                    zIndex={15}
                    width="130px"
                    minWidth="110px"
                    maxWidth="150px"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                  >
                    Materiality ({getMaterialNameForMetric(actualId)})
                  </Th>
                )}

                {/* Analysis columns - show if this actual has analysis_value */}
                {actualData.analysis_value && sortAnalysisTypes(Object.entries(actualData.analysis_value)).map(([analysisType, analysisData]) => (
                  <Th 
                    key={`${actualId}-analysis-${analysisType}`}
                    color="gray.600"
                    fontSize={isChatOpen ? "2xs" : "xs"}
                    bg="gray.50"
                    fontWeight="700"
                    textAlign="right"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    position="sticky"
                    top="0"
                    px={2}
                    py={isChatOpen ? 1 : 1}
                    left="auto"
                    zIndex={15}
                    width="120px"
                    minWidth="100px"
                    maxWidth="140px"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                  >
                    {analysisData?.name? analysisData?.name: getAnalysisColumnDisplayName(analysisType)}
                  </Th>
                ))}

                {/* Benchmarks */}
                {sortBenchmarks(Object.entries(actualData.benchmarks)).map(([benchmarkId, benchmark]) => (
                  <React.Fragment key={`${actualId}-${benchmarkId}`}>
                    <Th 
                      cursor="pointer" 
                      onClick={() => toggleBenchmark(`${actualId}-${benchmarkId}`)}
                      color="gray.600"
                      fontSize={isChatOpen ? "2xs" : "xs"}
                      bg="gray.50"
                      fontWeight="700"
                      textAlign="right"
                      position="sticky"
                      top="0"
                      px={2}
                      py={isChatOpen ? 1 : 1}
                      left="auto"
                      zIndex={15}
                      width="130px"
                      minWidth="110px"
                      borderBottom="1px solid"
                      borderColor="gray.200"
                    >
                      {
                        benchmark.column_name ?
                          benchmark.column_name :
                          (benchmarksIdNameMap && benchmarksIdNameMap[benchmarkId] ?
                            benchmarksIdNameMap[benchmarkId] : "Benchmark " + benchmarkId.substr(0,3))}{" "}
                      <Tooltip
                        label={showFilterForVersusBenchmark(benchmarkId, kgObservation!)}
                        placement="top"
                      >
                        <IconButton 
                          aria-label="Filters"
                          icon={<FiFilter size="10px" />}
                          size="xs"
                          variant="ghost"
                          minW="16px"
                          height="16px"
                          p={0}
                        />
                      </Tooltip>
                      {expanded.benchmarks.has(`${actualId}-${benchmarkId}`) ? <ChevronDownIcon ml={1} /> : <ChevronRightIcon ml={1} />}
                    </Th>

                    {expanded.benchmarks.has(`${actualId}-${benchmarkId}`) && 
                      sortAnalysisTypes(Object.entries(benchmark.analysis_value)).map(([analysisType, analysisData]) => (
                        <Th 
                          key={`${actualId}-${benchmarkId}-${analysisType}`}
                          color="gray.600"
                          fontSize={isChatOpen ? "2xs" : "xs"}
                          bg="gray.50"
                          fontWeight="700"
                          textAlign="right"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          position="sticky"
                          top="0"
                          px={2}
                          py={isChatOpen ? 1 : 1}
                          left="auto"
                          zIndex={15}
                          width="120px"
                          minWidth="100px"
                          maxWidth="140px"
                          borderBottom="1px solid"
                          borderColor="gray.200"
                        >
                          {analysisData?.name ? analysisData?.name : getAnalysisColumnDisplayName(analysisType)}
                        </Th>
                      ))}
                  </React.Fragment>
                ))}
              </>
            )}
          </React.Fragment>
        ))}
      </Tr>
    );
  };

  const renderTableRow = (rowIndex: number) => {
    if (!actualsMap || !actualsIdNameMap) return null;

    const sortedActuals = sortActualsByType(Object.entries(actualsMap));

    return (
      <Tr 
        bg="bg.surface"
        _hover={{ bg: "gray.100" }}
      >
        {nonMeasureValues?.map((nonMeasure, idx) => {
          let dataValue;
          if (nonMeasure.data_value_json_array) {
            dataValue = JSON.parse(nonMeasure.data_value_json_array);
          }
          const isFirstColumn = idx === 0;
          const isSelectedRow = selectedRowIndex === rowIndex;
          const isNegative = typeof dataValue[rowIndex] === "string" && dataValue[rowIndex].startsWith("(");
          const isNaNValue = Number.isNaN(dataValue[rowIndex]);
          const color = !isNaNValue && dataValue[rowIndex] !== null ? (isNegative ? "red" : "green") : "inherit";

          return (
            <Td 
              key={nonMeasure.column_id}
              color={color}
              textAlign={isFirstColumn && typeof dataValue[rowIndex] !== "number" ? "left" : "right"}
              p={0}
              pr={isFirstColumn ? 0 : 2}
              m={0}
              width={idx === 0 ? "200px" : "150px"}
              minWidth={idx === 0 ? "150px" : "120px"}
              maxWidth={idx === 0 ? "300px" : "200px"}
              whiteSpace={isFirstColumn ? "normal" : "nowrap"}
              overflow={isFirstColumn ? "visible" : "hidden"}
              textOverflow="ellipsis"
              position={isFirstColumn ? "sticky" : "static"}
              left={isFirstColumn ? "0" : "auto"}
              zIndex={isFirstColumn ? 12 : "auto"}
              cursor={isFirstColumn ? "pointer" : "auto"}
              bg={isFirstColumn ? "white" : "inherit"}
            >
              {isFirstColumn ? (
                showEditSuggestions ? (
                  <Menu closeOnSelect={false} placement="bottom-start" flip={true} onClose={handleMenuClose} isOpen={selectedRowIndex === rowIndex}>
                    <MenuButton
                      as={Button}
                      rightIcon={
                        <Box _groupHover={{ "& > svg": { boxSize: 5, color: "white" } }}>
                          <Icon 
                            as={IoMdAdd} 
                            color="white"
                            boxSize={5}
                            transition="all 0.2s"
                          />
                        </Box>
                      }
                      role="group"
                      onClick={() => handleMenuOpen(rowIndex)}
                      px={1}
                      m={0}
                      height="auto"
                      minHeight="20px"
                      color={isFirstColumn && isSelectedRow ? "white" : "black"}
                      cursor="pointer"
                      width="100%"
                      textAlign="left"
                      display="flex"
                      justifyContent="space-between"
                      _hover={{ bg: !treePannelLoader ? "purple.700" : "inherit", color: !treePannelLoader ? "white" : "black" }}
                      isDisabled={treePannelLoader}
                      bg={isSelectedRow && isFirstColumn ? "purple.700" : "inherit"}
                      wordBreak="break-word"
                      whiteSpace="normal"
                    >
                      <Text
                        as="span" 
                        fontSize={isChatOpen ? "xs" : "sm"} 
                        m={0} 
                        ml={1} 
                        p={0}
                        textAlign="left"
                        display="block"
                        width="100%"
                      >
                        {dataValue[rowIndex]}
                      </Text>
                    </MenuButton>
                    {!isOverlay && (
                    <Portal>
                      <EditTreeSuggestions
                        node={node}
                        listOfSuggestions={listOfSuggestions}
                        newSuggestionMap={newSuggestionMap}
                        setNewSuggestionMap={setNewSuggestionMap}
                        selectedRowIndex={selectedRowIndex}
                        melted={melted}
                        handleMenuClose={handleMenuClose}
                      />
                    </Portal>
                    )}
                  </Menu>
                ) : (
                  <Text
                    as="span" 
                    fontSize={isChatOpen ? "xs" : "sm"} 
                    m={0} 
                    ml={1} 
                    p={0}
                    textAlign="left"
                    display="block"
                    width="100%"
                  >
                    {typeof dataValue[rowIndex] === "number" ? dataValue[rowIndex].toLocaleString() : formatWithProto(dataValue[rowIndex])}
                  </Text>
                )
              ) : (
                <Text
                  as="span" 
                  fontSize={isChatOpen ? "xs" : "sm"} 
                  m={0} 
                  ml={1} 
                  p={0}
                  textAlign="left"
                  display="block"
                  width="100%"
                >
                  {dataValue[rowIndex]}
                </Text>
              )}
            </Td>
          );
        })}

        {sortedActuals.map(([actualId, actualData]) => (
          <React.Fragment key={actualId}>
            <Td
              color="inherit"
              textAlign="right"
              p={0}
              pr={2}
              m={0}
              width="140px"
              minWidth="120px"
              maxWidth="180px"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              position="static"
              left="auto"
              zIndex="auto"
              cursor="auto"
              bg="inherit"
            >
              {formatWithProto(
                actualData.actual_value[rowIndex],
                getMetricFormat(actualId, node.current_node, isMaterialColumn(actualId, node.current_node)),
                { isMaterial: isMaterialColumn(actualId, node.current_node) }
              )}
            </Td>

            {expanded?.actuals?.has(actualId) && (
              <>
                {/* Material cell - show if this actual has a material */}
                {getMaterialNameForMetric(actualId) && (
                  <Td
                    color="inherit"
                    textAlign="right"
                    p={0}
                    pr={2}
                    m={0}
                    width="130px"
                    minWidth="110px"
                    maxWidth="150px"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    position="static"
                    left="auto"
                    zIndex="auto"
                    cursor="auto"
                    bg="inherit"
                  >
                    {(() => {
                      const materialValue = actualData.material_value ? actualData.material_value[rowIndex] : null;
                      const materialId = getMaterialIdForMetric(actualId, node.current_node);
                      const materialFormatProto = materialId ? getMetricFormat(materialId, node.current_node, true) : undefined;
                      return materialValue !== null ? formatWithProto(materialValue, materialFormatProto, { isMaterial: true }) : "-";
                    })()}
                  </Td>
                )}

                {/* Analysis cells - show if this actual has analysis_value */}
                {actualData.analysis_value && sortAnalysisTypes(Object.entries(actualData.analysis_value)).map(([analysisType, analysisData]) => (
                  <Td 
                    key={`${actualId}-analysis-${analysisType}`}
                    color={analysisData.values[rowIndex] < 0 ? "red.500" : analysisData.values[rowIndex] >= 0 ? "green.500" : "inherit"}
                    textAlign="right"
                    p={0}
                    pr={2}
                    m={0}
                    width="120px"
                    minWidth="100px"
                    maxWidth="140px"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    position="static"
                    left="auto"
                    zIndex="auto"
                    cursor="auto"
                    bg="inherit"
                  >
                    {formatWithProto(
                      analysisData.values[rowIndex],
                      getMetricFormat(actualId, node.current_node),
                      {
                        isCompositionPercentage: analysisType === "COMPOSITION_PERCENTAGE",
                        isRank: analysisType === "RANK",
                        isAnalysisColumnType: true
                      }
                    )}
                  </Td>
                ))}

                {/* Benchmarks */}
                {sortBenchmarks(Object.entries(actualData.benchmarks)).map(([benchmarkId, benchmark]) => (
                  <React.Fragment key={`${actualId}-${benchmarkId}`}>
                    <Td
                      color="inherit"
                      textAlign="right"
                      p={0}
                      pr={2}
                      m={0}
                      width="130px"
                      minWidth="110px"
                      maxWidth="150px"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      position="static"
                      left="auto"
                      zIndex="auto"
                      cursor="auto"
                      bg="inherit"
                    >  
                      {formatWithProto(
                        benchmark.benchmark_value[rowIndex],
                        getMetricFormat(actualId, node.current_node)
                      )}
                    </Td>

                    {expanded?.benchmarks?.has(`${actualId}-${benchmarkId}`) && 
                      sortAnalysisTypes(Object.entries(benchmark.analysis_value)).map(([analysisType, analysisData]) => (
                        <Td 
                          key={`${actualId}-${benchmarkId}-${analysisType}`}
                          color={analysisData.values[rowIndex] < 0 ? "red.500" : analysisData.values[rowIndex] >= 0 ? "green.500" : "inherit"}
                          textAlign="right"
                          p={0}
                          pr={2}
                          m={0}
                          width="120px"
                          minWidth="100px"
                          maxWidth="140px"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          position="static"
                          left="auto"
                          zIndex="auto"
                          cursor="auto"
                          bg="inherit"
                        >
                          {formatWithProto(
                            analysisData.values[rowIndex],
                            getMetricFormat(actualId, node.current_node),
                            {
                              isVariancePercentage: analysisType === "VARIANCE_PERCENTAGE",
                              isAccomplishPercentage: analysisType === "ACCOMPLISH_PERCENTAGE",
                              isCompositionPercentage: analysisType === "COMPOSITION_PERCENTAGE",
                              isRank: analysisType === "RANK",
                              isPercentageMetricVariance: (analysisType === "VARIANCE" && !!node.current_node.kg_observation?.metrics.find(m => m.header?.id === actualId)?.format?.percentage),
                              isAnalysisColumnType: true
                            }
                          )}
                        </Td>
                      ))}
                  </React.Fragment>
                ))}
              </>
            )}
          </React.Fragment>
        ))}
      </Tr>
    );
  };

  return (
    <Box 
      p={0} 
      height="100%" 
      minHeight={0} 
      display="flex" 
      flexDirection="column"
      overflow="hidden"
    >
      {melted ? 
        <MeltedDataTable
          node={node}
          isChatOpen={isChatOpen}
          benchmarksIdNameMap={benchmarksIdNameMap}
          actualsIdNameMap={actualsIdNameMap}
          actualsMap={actualsMap}
          handleMenuClose={handleMenuClose}
          handleMenuOpen={handleMenuOpen}
          selectedMeltedRowId={selectedMeltedRowId}
          listOfSuggestions={listOfSuggestions}
          newSuggestionMap={newSuggestionMap}
          setNewSuggestionMap={setNewSuggestionMap}
          treePannelLoader={treePannelLoader}
          isOverlay={isOverlay}
        />
        : (
        actualsMap && actualsIdNameMap && (
          <Box
            p={0}
            key={node && node.current_node.id}
            flex="1"
            minHeight={0}
            display="flex"
            flexDirection="column"
            height="100%"
            maxWidth="100%"
            maxHeight="300px"
            overflowY="auto"
            overflowX="auto"
            position="relative"
            sx={{
              scrollbarWidth: "thin",
              scrollbarColor: "#CBD5E0 transparent",
              "&::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#CBD5E0",
                borderRadius: "8px",
              },
              "&:hover": {
                cursor: "default"
              }
            }}
            m={0}
          >
            <Table 
              size={isChatOpen ? "sm" : "md"} 
              variant="simple" 
              w="100%" 
              h="100%" 
              align="center"
              sx={{
                tableLayout: "auto",
                minWidth: "max-content",
                borderCollapse: "separate",
                borderSpacing: 0,
                // Fix for sticky headers - ensure they stay fixed during horizontal scroll
                "& thead": {
                  position: "sticky",
                  top: 0,
                  zIndex: 15,
                  backgroundColor: "white",
                  //boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                },
                "& thead th": {
                  position: "sticky !important",
                  top: "0 !important",
                  backgroundColor: "white !important",
                  zIndex: 15,
                  //boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                },
                // Ensure first column stays sticky horizontally as well
                "& thead th:first-of-type": {
                  position: "sticky !important",
                  left: "0 !important",
                  top: "0 !important",
                  zIndex: 20,
                  backgroundColor: "white !important",
                  //boxShadow: "2px 2px 4px rgba(0,0,0,0.1)"
                },
                "& tbody td:first-of-type": {
                  position: "sticky !important",
                  left: "0 !important",
                  zIndex: 12,
                  backgroundColor: "white !important",
                  //boxShadow: "2px 0 4px rgba(0,0,0,0.1)"
                }
              }}
            >
              <Thead>
                {renderTableHeader()}
              </Thead>
              <Tbody>
                {Array.from({ length: actualsMap[Object.keys(actualsMap)[0]]?.actual_value?.length}, (_, i) => 
                  renderTableRow(i)
                )}
              </Tbody>
            </Table>
          </Box>
        )
      )}
    </Box>
  );
};

export default DataTableMKG;